"use client";

import { startTransition, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { themeCookieName, type Theme } from "@/lib/theme";

type Props = {
  value: Theme;
  className?: string;
};

function setThemeCookie(theme: Theme) {
  const v = encodeURIComponent(theme);
  document.cookie = `${themeCookieName}=${v}; Path=/; Max-Age=${60 * 60 * 24 * 365}; SameSite=Lax`;
}

function applyDomTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.classList.toggle("dark", theme === "dark");
}

export function ThemeToggle({ value, className }: Props) {
  const router = useRouter();
  const [theme, setTheme] = useState<Theme>(value);

  useEffect(() => {
    setTheme(value);
  }, [value]);

  useEffect(() => {
    applyDomTheme(theme);
  }, [theme]);

  const label = useMemo(() => (theme === "dark" ? "Dark" : "Light"), [theme]);

  return (
    <button
      type="button"
      className={[
        "p-3 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700",
        className ?? "",
      ].join(" ")}
      aria-label={`Theme: ${label}`}
      role="switch"
      aria-checked={theme === "dark"}
      onClick={() => {
        const next = theme === "dark" ? "light" : "dark";
        setThemeCookie(next);
        applyDomTheme(next);
        startTransition(() => {
          setTheme(next);
        });
        router.refresh();
      }}
    >
      {/* Sun (shown in dark mode) */}
      <svg
        className="w-5 h-5 text-amber-500 hidden dark:block"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
      {/* Moon (shown in light mode) */}
      <svg
        className="w-5 h-5 text-slate-600 block dark:hidden"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
        />
      </svg>
    </button>
  );
}
